import { useTranslation } from "react-i18next";
import { showConfirmationDialog } from "@/components/staffingSearch/CreateShiftContent";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { backendService } from "@/services/backend.service";
import { type BaseRequest, ChangesSource, type IncentivePlan, type OpenShiftRequest } from "@/utils/models";
import { getOverlappingShift, isAgencyPostedShift } from "@/utils/utilFunctions";
import { useMergeRequests } from "./useMergeRequests";

/**
 * Add/edit a flat bonus on an open shift. When the resulting shift would match an existing one
 * (same time/role/etc + same bonus), prompts the user to merge — same UX as the post-time prompt
 * (`useHandleCheckOverlappingShifts`) and the time-edit prompt (`useUpdateRequestTime`).
 *
 * On merge: calls `/staffing/merge-requests` so pending pickups on either side are transferred to the surviving shift.
 * On keep / no match: writes the new flat bonus via the dedicated `flat-bonus` endpoint.
 */
export const useUpdateRequestFlatBonus = () => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { staffRequests, setStaffRequests, refreshRequests } = useRequestsContext();
	const { mergeRequests } = useMergeRequests();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const updateFlatBonus = async ({
		request,
		bonusAmount,
		onLocalUpdate,
		onDone,
	}: {
		request: BaseRequest;
		bonusAmount: number;
		// Caller-controlled local-state patches (for the parent table's optimistic update).
		onLocalUpdate?: (patch: { bonus: number; current_bonus: number; incentive_windows: IncentivePlan[] }) => void;
		onDone?: () => void;
	}): Promise<void> => {
		const newWindows: IncentivePlan[] = bonusAmount > 0 ? [{ value: bonusAmount, startHour: 0, endHour: null }] : [];
		const hadBonus = (request.incentive_windows?.length ?? 0) > 0;

		// Adding 0 to a shift that already has no bonus is a no-op — skip the API call entirely.
		if (bonusAmount === 0 && !hadBonus) {
			onDone?.();
			return;
		}

		// Always check for overlap — even when removing a bonus, a no-bonus shift may now match
		// another no-bonus shift at the same time/role and be eligible to merge.
		const otherRequests = (staffRequests.filter((r) => r.id !== request.id) as unknown as OpenShiftRequest[]).filter((r) => "needed_nurses" in r);
		const matching = getOverlappingShift({
			staffRequests: otherRequests,
			startTime: request.message.shift_start,
			endTime: request.message.shift_end,
			roles: request.message.roles,
			jobManagementEnabled,
			jobId: request.job_id,
			intendedBonusWindows: newWindows,
			chargeOnly: request.charge_only ?? false,
			location: request.location,
			postToAgency: isAgencyPostedShift(request as OpenShiftRequest),
			subRole: request.sub_role,
			agencySourceName: (request as OpenShiftRequest).agency_source_name,
		}) as OpenShiftRequest | undefined;

		if (matching) {
			let shouldMerge = false;
			const thisNeeded = (request as OpenShiftRequest).needed_nurses ?? 1;
			await showConfirmationDialog({
				title: "Overlapping Open Shift",
				content: "A similar open shift already exists with the same bonus. Would you like to merge them?",
				actions: [
					{ label: `Keep separate (${thisNeeded})`, onClick: () => {} },
					{
						label: `Merge (${matching.needed_nurses + thisNeeded})`,
						onClick: () => {
							shouldMerge = true;
						},
					},
				],
			});

			if (shouldMerge) {
				try {
					await mergeRequests({
						request,
						matching,
						newNeededNurses: matching.needed_nurses + thisNeeded,
						source: ChangesSource.Requests,
						successMessageKey: "bonus.updated.success",
					});
					onDone?.();
				} catch (error) {
					showErrorSnackbar("Failed to merge shifts", { action: "merge shifts failed", request_id: request.id }, error);
				}
				return;
			}
		}

		try {
			// bonusAmount === 0 means "no bonus" — route to delete so the snapshot is gone instead of
			// becoming a `flat:0` row (which has a different merge key from `none`).
			if (bonusAmount === 0) {
				await backendService.deleteRequestFlatBonus(chosenDepartment.department_id, request.id);
			} else {
				await backendService.setRequestFlatBonus(chosenDepartment.department_id, request.id, bonusAmount);
			}
			onLocalUpdate?.({ bonus: bonusAmount, current_bonus: bonusAmount, incentive_windows: newWindows });
			setStaffRequests((prev) =>
				prev.map((req) => (req.id === request.id ? { ...req, bonus: bonusAmount, current_bonus: bonusAmount, incentive_windows: newWindows } : req)),
			);
			showSnackbar(t("bonus.updated.success"), "success");
			await refreshRequests();
			onDone?.();
		} catch (error) {
			showErrorSnackbar("Failed to update request bonus", { action: "update request bonus failed", request_id: request.id }, error);
		}
	};

	return { updateFlatBonus };
};
